/**
 * Prashasta Event Journey — Step Sequencing Engine
 * Computes the ordered step list from current journey state.
 * Supports conditional branching — never hardcodes role/type assumptions in JSX.
 */
import { STEP_STAGES, JOURNEY_STAGES } from './journeyConfig';

// ── Storage ───────────────────────────────────────────────────────────────────
const STORAGE_KEY = 'prashasta_event_journey';

// ── Source Attribution ────────────────────────────────────────────────────────
export function captureUTMs() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utms = {
      source_campaign: params.get('utm_campaign') || null,
      source_medium:   params.get('utm_medium')   || null,
      source_term:     params.get('utm_term')      || null,
      source_content:  params.get('utm_content')   || null,
    };
    // Only return if any UTM present
    return Object.values(utms).some(Boolean) ? utms : null;
  } catch { return null; }
}

/**
 * Initialise source attribution for a new (or first-visit) journey.
 * IMPORTANT: never overwrites existing source data — original source is permanent.
 */
export function initJourneySource(entryPoint = 'DIRECT', existingState = null) {
  // If source already captured, preserve it
  if (existingState?.source_captured) return {};

  const utms = captureUTMs();
  return {
    source_channel:      'WEBSITE',
    source_touchpoint:   'BUILD_MY_EVENT',
    source_entry_point:  entryPoint,
    source_captured:     true,
    source_first_seen:   new Date().toISOString(),
    ...(utms || {}),
  };
}

/**
 * Add an entry to the journey path (deduplicated).
 * Path is an ordered array of touchpoints: ['WEBSITE', 'DATE_CHECK', 'BUILD_MY_EVENT']
 */
export function addJourneyPathEntry(state, entry) {
  const path = state?.journey_path || [];
  if (path[path.length - 1] === entry) return path; // no duplicate consecutive entries
  return [...path, entry];
}

export function loadJourneyFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveJourneyToStorage(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...state,
      last_updated_at: new Date().toISOString(),
    }));
  } catch { /* ignore quota errors */ }
}

export function clearJourneyFromStorage() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

// ── Step sequencing ───────────────────────────────────────────────────────────
export function getStepSequence(state) {
  const steps = ['EVENT_TYPE'];

  const eventType = state?.event_type?.value;
  if (eventType === 'WEDDING') {
    steps.push('WEDDING_FUNCTIONS');
  } else if (eventType === 'CONFERENCE') {
    steps.push('CONFERENCE_TYPE');
  }

  steps.push('PLANNING_SCOPE');

  const scope = state?.planning_scope?.value;
  if (scope === 'END_TO_END') {
    steps.push('SCOPE_CONFIRM');
  } else if (scope === 'CUSTOM') {
    steps.push('SERVICE_SELECTION');
  }

  steps.push('CITY');

  // Style direction: show for weddings, engagements, birthdays, awards, or if decor/entertainment in services
  const services = state?.service_interests?.value || [];
  const showStyle =
    ['WEDDING', 'ENGAGEMENT', 'BIRTHDAY', 'AWARDS'].includes(eventType) ||
    scope === 'END_TO_END' ||
    services.some(s => ['DECOR', 'ENTERTAINMENT', 'PHOTOGRAPHY'].includes(s));

  if (showStyle) steps.push('STYLE_PREF');

  steps.push('GUEST_COUNT');
  steps.push('VENUE_STATUS');

  // Skip date question if already captured from homepage check
  if (!state?.event_date?.confirmed) {
    steps.push('DATE');
  }

  steps.push('ESTIMATE');
  steps.push('CONTACT');
  steps.push('RESULT');

  return steps;
}

// ── Progress computation ──────────────────────────────────────────────────────
export function computeProgress(state, currentStepId) {
  const sequence = getStepSequence(state);
  const totalSteps = sequence.length - 1; // exclude RESULT
  const currentIdx = sequence.indexOf(currentStepId);

  const completionPct = currentStepId === 'RESULT'
    ? 100
    : Math.round((Math.max(currentIdx, 0) / totalSteps) * 100);

  const currentStage = STEP_STAGES[currentStepId] || 'EVENT';
  const currentStageIdx = JOURNEY_STAGES.findIndex(s => s.id === currentStage);

  return {
    pct: completionPct,
    currentStage,
    stages: JOURNEY_STAGES.map((stage, i) => ({
      ...stage,
      status: i < currentStageIdx ? 'DONE' : i === currentStageIdx ? 'ACTIVE' : 'PENDING',
    })),
  };
}

// ── Navigation helpers ────────────────────────────────────────────────────────
export function getNextStep(state, currentStepId) {
  // Edge case: when advancing FROM the DATE step, the caller has already set
  // event_date.confirmed = true, which causes getStepSequence to omit 'DATE'
  // from the sequence (by design — skip date if already confirmed).
  // We must compute the sequence as if the date is not yet confirmed so that
  // 'DATE' appears in the sequence and we can find its successor.
  const stateForLookup =
    currentStepId === 'DATE' && state?.event_date?.confirmed
      ? { ...state, event_date: { ...state.event_date, confirmed: false } }
      : state;
  const sequence = getStepSequence(stateForLookup);
  const idx = sequence.indexOf(currentStepId);
  if (idx === -1) return null; // current step not present in sequence — do not navigate
  return idx < sequence.length - 1 ? sequence[idx + 1] : null;
}

export function getPrevStep(state, currentStepId) {
  const sequence = getStepSequence(state);
  const idx = sequence.indexOf(currentStepId);
  return idx > 0 ? sequence[idx - 1] : null;
}

// ── Journey completion score ──────────────────────────────────────────────────
export function computeCompletionScore(state) {
  const fields = [
    'event_type', 'planning_scope', 'city',
    'guest_count_range', 'venue_status',
  ];
  const completed = fields.filter(f => state?.[f]?.value).length;
  const hasDate = state?.event_date?.value ? 1 : 0;
  const hasEstimate = state?.estimate_ref ? 1 : 0;
  const hasServices = (state?.service_interests?.value?.length || 0) > 0 ? 1 : 0;
  const hasStyle = (state?.style_preferences?.value?.length || 0) > 0 ? 1 : 0;
  const hasContact = state?.contact?.name && state?.contact?.mobile ? 2 : 0;

  const total = completed + hasDate + hasEstimate + hasServices + hasStyle + hasContact;
  return Math.min(Math.round((total / 11) * 100), 100);
}

// ── Build a WhatsApp prefill message from journey state ───────────────────────
export function buildWhatsAppMessage(state) {
  const name = state?.contact?.name || '';
  const eventType = state?.event_type?.label || state?.event_type?.value || 'an event';
  const city = state?.city?.display || state?.city_custom || '';
  const guests = state?.guest_count_range?.display || '';
  const scope = state?.planning_scope?.value === 'END_TO_END' ? 'full end-to-end planning' : 'specific services';
  const services = (state?.service_interests?.value || [])
    .map(s => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' '))
    .slice(0, 3).join(', ');
  const date = state?.event_date?.display
    ? `on ${state.event_date.display}`
    : state?.event_date?.value ? '' : '';

  let msg = `Hi Prashasta,`;
  if (name) msg += ` I'm ${name}.`;
  msg += `\n\nI'm planning a ${eventType}`;
  if (city) msg += ` in ${city}`;
  if (date) msg += ` ${date}`;
  if (guests) msg += ` for around ${guests} guests`;
  msg += `.`;

  if (scope) msg += `\n\nI'm looking for ${scope}`;
  if (services) msg += `, particularly help with ${services}`;
  msg += `.`;

  msg += `\n\nI've already started my Event Plan and would love to continue with a Prashasta planner.`;

  return msg.trim();
}
